import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../lib/api";
import { formatMoney } from "../../lib/directMailApi";
import { getApiErrorMessage } from "../../lib/apiError";
import { ResponsiveTable } from "../common/ResponsiveTable";
interface PerformanceRow {
  id: number;
  name: string;
  pinned: number;
  approved: number;
  mailed: number;
  scanned: number;
  leads: number;
  known_mail_spend: string;
  cost_per_lead: string | null;
}
export function MailPerformanceExplorer() {
  const [from, setFrom] = useState(""),
    [through, setThrough] = useState(""),
    [selected, setSelected] = useState<number[]>([]),
    [error, setError] = useState("");
  const params = new URLSearchParams({
    ...(from ? { from } : {}),
    ...(through ? { through } : {}),
  });
  const query = useQuery({
    queryKey: ["direct-mail", "performance", from, through],
    queryFn: async () =>
      (
        await apiClient.get<{ basis: string; results: PerformanceRow[] }>(
          `/api/marketing/direct-mail/campaigns/performance/?${params}`,
        )
      ).data,
  });
  const rows = (query.data?.results ?? []).filter(
    (row) => !selected.length || selected.includes(row.id),
  );
  const csv = () => {
    try {
      const fields = [
        "name",
        "pinned",
        "approved",
        "mailed",
        "scanned",
        "leads",
        "known_mail_spend",
        "cost_per_lead",
      ] as const;
      const escape = (v: unknown) => {
        let text = String(v ?? "");
        if (/^[=+\-@\t\r]/.test(text)) text = "'" + text;
        return '"' + text.replaceAll('"', '""') + '"';
      };
      const content = [
        fields.join(","),
        ...rows.map((row) => fields.map((key) => escape(row[key])).join(",")),
      ].join("\r\n");
      const url = URL.createObjectURL(
        new Blob([content], { type: "text/csv;charset=utf-8;" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "direct-mail-performance.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(getApiErrorMessage(e, "Export failed."));
    }
  };
  return (
    <Card sx={{ my: 3 }}>
      <Stack spacing={2}>
        <Typography level="h3">Campaign performance explorer</Typography>
        <Typography level="body-sm">
          Choose when houses were pinned, then compare their progress and known
          mailing spend.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <FormControl>
            <FormLabel>Pinned from</FormLabel>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Pinned through</FormLabel>
            <Input
              type="date"
              value={through}
              onChange={(e) => setThrough(e.target.value)}
            />
          </FormControl>
          <Button variant="outlined" onClick={csv} disabled={!rows.length}>
            Export comparison CSV
          </Button>
        </Stack>
        {(error || query.isError) && (
          <Alert color="danger">
            {error ||
              getApiErrorMessage(query.error, "Report could not be loaded.")}
            <Button onClick={() => void query.refetch()}>Retry</Button>
          </Alert>
        )}
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {query.data?.results.map((row) => (
            <Checkbox
              key={row.id}
              label={row.name}
              checked={selected.includes(row.id)}
              onChange={(e) =>
                setSelected(
                  e.target.checked
                    ? [...selected, row.id]
                    : selected.filter((id) => id !== row.id),
                )
              }
            />
          ))}
        </Stack>
        <Typography level="body-xs">
          Select campaigns to compare; no selection shows all campaigns.
        </Typography>
        {rows.map((row) => (
          <Box key={row.id}>
            <Typography level="title-sm">{row.name}</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {(
                ["pinned", "approved", "mailed", "scanned", "leads"] as const
              ).map((key) => (
                <Box
                  key={key}
                  sx={{
                    flex: "1 1 100px",
                    p: 1,
                    bgcolor: "background.level1",
                    borderRadius: "sm",
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {key}
                  </Typography>
                  <Typography level="h4">{row[key]}</Typography>
                  <Box sx={{ height: 4, bgcolor: "primary.200", mt: 1 }}>
                    <Box
                      sx={{
                        height: "100%",
                        width: `${row.pinned ? Math.min(100, (100 * row[key]) / row.pinned) : 0}%`,
                        bgcolor: "primary.600",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
        <ResponsiveTable<PerformanceRow>
          rows={rows}
          getRowKey={(row) => row.id}
          columns={[
            { key: "name", header: "Campaign", render: (row) => row.name },
            {
              key: "known_mail_spend",
              header: "Known mail spend",
              render: (row) => formatMoney(row.known_mail_spend),
            },
            {
              key: "leads",
              header: "Unique leads",
              render: (row) => row.leads,
            },
            {
              key: "cost_per_lead",
              header: "Mail cost / lead",
              render: (row) =>
                row.cost_per_lead === null
                  ? "No leads yet"
                  : formatMoney(row.cost_per_lead),
            },
          ]}
        />
        <Typography level="body-xs">
          {query.data?.basis} Scanned counts unique houses, and leads count
          unique linked contacts. Stages are separate signals; a manual claim
          can exist before mailing is reported.
        </Typography>
      </Stack>
    </Card>
  );
}
